type DrawingMode = 'circle' | 'polygon' | 'none';

const METERS_PER_DEGREE = 111320;

const getOverlayArea = (overlay: any): number => {
  if (overlay instanceof google.maps.Circle) {
    const radius = overlay.getRadius() || 0;
    return Math.PI * radius * radius;
  }
  if (overlay instanceof google.maps.Polygon) {
    const path = overlay.getPath();
    if (google.maps.geometry?.spherical) {
      return google.maps.geometry.spherical.computeArea(path);
    }
    const points = path.getArray();
    if (points.length === 0) return 0;
    const lats = points.map(p => p.lat());
    const lngs = points.map(p => p.lng());
    const midLat = (Math.max(...lats) + Math.min(...lats)) / 2;
    return (Math.max(...lats) - Math.min(...lats)) * METERS_PER_DEGREE
      * (Math.max(...lngs) - Math.min(...lngs)) * METERS_PER_DEGREE * Math.cos(midLat * Math.PI / 180);
  }
  return 0;
};

// Stack overlapping shapes so smaller ones sit on top and receive clicks first;
// a larger shape stays clickable in the area outside the smaller ones.
export const applySizeBasedZIndex = (overlays: any[]) => {
  [...overlays]
    .sort((a, b) => getOverlayArea(b) - getOverlayArea(a))
    .forEach((overlay, index) => overlay.setOptions({ zIndex: index + 1 }));
};

const SHAPE_OPTIONS: google.maps.CircleOptions & google.maps.PolygonOptions = {
  fillColor: 'gray',
  fillOpacity: 0.3,
  strokeWeight: 2,
  strokeColor: 'gray',
  clickable: false,
  editable: true,
};

export class CustomDrawingManager {
  private map: google.maps.Map;
  private maps: google.maps;
  private mode: DrawingMode = 'none';
  private clickListener: google.maps.MapsEventListener | null = null;
  private mouseMoveListener: google.maps.MapsEventListener | null = null;
  private setAtListener: google.maps.MapsEventListener | null = null;
  private insertAtListener: google.maps.MapsEventListener | null = null;
  private polygon: google.maps.Polygon | null = null;
  private overlayCompleteCallback?: (overlay: google.maps.Circle | google.maps.Polygon) => void;
  private drawingModeChangeCallback?: (mode: DrawingMode) => void;

  // For polygon drawing
  private polyPath: google.maps.LatLng[] = [];
  private previewPolyline: google.maps.Polyline | null = null;

  // For circle drawing
  private circleCenter: google.maps.LatLng | null = null;
  private previewCircle: google.maps.Circle | null = null;

  constructor(map: google.maps.Map, maps: google.maps) {
    this.map = map;
    this.maps = maps;
  }

  setDrawingMode(mode: DrawingMode) {
    this.clearListeners();
    this.clearPreview();
    this.mode = mode;
    this.map.setOptions({ draggableCursor: mode !== 'none' ? 'crosshair' : '' });

    if (mode === 'polygon') this.startPolygonDrawing();
    if (mode === 'circle') this.startCircleDrawing();

    this.drawingModeChangeCallback?.(mode);
  }

  getDrawingMode(): DrawingMode {
    return this.mode;
  }

  addOverlayCompleteListener(
    callback: (overlay: google.maps.Circle | google.maps.Polygon) => void
  ) {
    this.overlayCompleteCallback = callback;
  }

  // Clickable overlays swallow map click/mousemove events, so callers should
  // disable clickability on existing shapes while a drawing mode is active.
  addDrawingModeChangeListener(callback: (mode: DrawingMode) => void) {
    this.drawingModeChangeCallback = callback;
  }

  isDrawing(): boolean {
    return this.mode !== 'none';
  }

  // ─── Circle Drawing ──────────────────────────────────────────────────────────

  private startCircleDrawing() {
    this.clickListener = this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!this.circleCenter) {
        // First click: set center
        this.circleCenter = e.latLng!;
        this.previewCircle = new google.maps.Circle({
          ...SHAPE_OPTIONS,
          editable: false,
          center: this.circleCenter,
          radius: 0,
          map: this.map,
        });

        this.mouseMoveListener = this.map.addListener('mousemove', (me: google.maps.MapMouseEvent) => {
          const radius = google.maps.geometry.spherical.computeDistanceBetween(
            this.circleCenter!,
            me.latLng!
          );
          this.previewCircle?.setRadius(radius);
        });
      } else {
        // Second click: finalize circle
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
          this.circleCenter,
          e.latLng!
        );
        this.clearPreview();

        const circle = new google.maps.Circle({
          ...SHAPE_OPTIONS,
          center: this.circleCenter,
          radius,
          map: this.map,
        });

        this.circleCenter = null;
        this.overlayCompleteCallback?.(circle);
        this.setDrawingMode('none');
      }
    });
  }

  // ─── Polygon Drawing ─────────────────────────────────────────────────────────

  private startPolygonDrawing() {
    this.polyPath = [];

    this.polygon = new google.maps.Polygon({
      path: this.polyPath,
      ...SHAPE_OPTIONS,
      editable: true, // Allows fine-tuning vertices later
      map: this.map,
    });

    this.clickListener = this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
      this.polygon.getPath().push(e.latLng!);

      this.logVertices();

    });

    this.setAtListener = this.maps.event.addListener(
      this.polygon.getPath(), "set_at", () => this.logVertices()
    );
    this.insertAtListener = this.maps.event.addListener(
      this.polygon.getPath(), "insert_at", () => this.logVertices()
    );
  }

  private logVertices() {
    if (!this.polygon) return;

    const path = this.polygon.getPath();
    const coordinates = [];

    for (let i = 0; i < path.getLength(); i++) {
      const xy = path.getAt(i);
      coordinates.push({ lat: xy.lat(), lng: xy.lng() });
    }

    console.log("Current Vertices:", coordinates);

    if( coordinates.length > 2 ){
      this.overlayCompleteCallback?.(this.polygon);
    }
  }

  // ─── Markers ─────────────────────────────────────────────────────────────────


  // ─── Helpers ─────────────────────────────────────────────────────────────────

  private clearListeners() {
    this.clickListener?.remove();
    this.mouseMoveListener?.remove();
    this.setAtListener?.remove();
    this.insertAtListener?.remove();
    this.clickListener = null;
    this.mouseMoveListener = null;
    this.setAtListener = null;
    this.insertAtListener = null;
  }

  private clearPreview() {
    this.previewCircle?.setMap(null);
    this.previewCircle = null;
    this.polygon?.setMap(null);
    this.polygon = null;
  }

  setMap(map: google.maps.Map | null) {
    if (!map) {
      this.clearListeners();
      this.clearPreview();
    }
  }
}
